
import updateFeatureName from "@/app/infrastructures/misc/common-library/UpdateFeatureName";
import { GTMNotificationEvent } from "@/app/infrastructures/misc/gtm-event/custom-events/notification-events";
import {
  dateToTime,
  firstCapitalize,
  initialName,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import {
  DeviceAuthReviewRequest,
  PostCsAccountApiRequest
} from "@/data/payload/api/AccountRequest";
import { AccountStatus } from "@/domain/entities/Account";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { NotificationMetaLogin } from "@/domain/entities/Notification";
import { flags } from "@/feature-flags";
import { debounce } from "lodash";
import isEmpty from "lodash/isEmpty";
import OverlayPanel from "primevue/overlaypanel";
import { Options, Vue, prop } from "vue-class-component";
import { AccountController } from "../../controllers/AccountController";
import { AuthController } from "../../controllers/AuthController";
import { MainAppController } from "../../controllers/MainAppController";
import { NotificationController } from "../../controllers/NotificationController";
import CustomOverlay from "./overlayPanelCustom.vue";
import { FcsPermissionManagementLoginApproval } from "@/feature-flags/flags-management-login-approval";
import { ManagementLoginApprovalController } from "../../controllers/ManagementLoginApprovalController";

type ActionType = "approve" | "reject";

class Prop {
  customBreadcrumbTitle = prop<string>({
    default: "",
    type: String
  });
}

@Options({
  components: {
    OverlayPanel,
    CustomOverlay,
  }
})
class Breadcrumb extends Vue.with(Prop) {
  isOpenModalLogout = false;
  get getCurrentRoute(): any {
    return this.$route;
  }
  get id() {
    if (!this.$route.meta.customPath) {
      return this.$route.params.id;
    }

    const title = this.$route.meta.title as string;
    if (title && typeof title === "string") {
      const translatedTitle = this.$t(title);
      if (translatedTitle !== title) {
        return translatedTitle;
      }
    }

    return this.$route.meta.name as string;
  }
  get params() {
    return this.$route.query;
  }

  get isGrayBreadcrumb() {
    return (
      this.$route.name === "kulioner-detail" ||
      this.$route.name === "kulioner-list"
    );
  }

  get isSemiboldBreadcrumb() {
    return [
      "pusat-resolusi-v2-list",
      "ticket_escalate",
      "my_ticket",
      "ticket_followed"
    ].includes(this.$route.name as string);
  }

  get decideBreadcrumbColor() {
    if (this.isGrayBreadcrumb) {
      return "text-gray-lp-500 font-semibold text-xs";
    }

    return "text-red-lp-2000 font-semibold text-xs";
  }

  get isPusatResolusiMenu() {
    return this.$router.currentRoute.value.path.includes("/pusat-resolusi-v2");
  }

  goHome() {
    this.$router.push(flags.flag_default_path_genesis.getValue());
  }
  goPath(path: any) {
    this.$router.push(path);
  }
  goDetail(path: any, id: string) {
    const detail = id && path.replace(":id", id);
    this.$router.push(detail);
  }
  get dataProfile() {
    return AccountController.accountData;
  }
  get initialName(): any {
    return initialName(this.dataProfile.name);
  }
  get isCustomerService(): boolean {
    return this.dataProfile.account_type === "customer-service";
  }
  get isClientAccount(): boolean {
    return this.dataProfile.isAllClient;
  }
  goAllNotification() {
    this.$router.push("/notifications");
  }
  goDetailNotification(id: number, isDeviceMaster: boolean) {
    if (isDeviceMaster) return;

    GTMNotificationEvent("notification_display_detail", {
      notificationId: id
    });
    this.$router.push(`/notifications/${id}`);
    NotificationController._getDetail(id);
  }
  onClickLogout() {
    this.isOpenModalLogout = true;
  }
  onClose() {
    this.isOpenModalLogout = false;
  }
  async onSubmit() {
    this.onClose();
    MainAppController.showLoading();
    await AuthController.logout();
    MainAppController.closeLoading();
  }

  getUnreadList = debounce(() => {
    NotificationController._getUnreadData(true);
    NotificationController._getNotificationSidebarData({
      page: 1,
      limit: 10,
      status: "",
      search: "",
      isPinned: 0,
      sort: "",
      cache: true
    });
  }, 500);
  get allData() {
    return NotificationController.groupNotificationSidebar;
  }
  get unreadData() {
    return NotificationController.unreadData;
  }
  get isNotificationsExist() {
    const data = NotificationController.groupNotificationSidebar;
    return !isEmpty(data);
  }
  initialNameNotification(value: string) {
    return initialName(value);
  }
  dateToTime(value: any): any {
    return dateToTime(value);
  }
  firstCapitalize(string: string) {
    return firstCapitalize(string);
  }

  get statusAccount(): AccountStatus {
    return AccountController.accountStatus;
  }
  async onClickToggleStatus(value: boolean) {
    try {
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();
      await AccountController.postCsAccountLog(
        new PostCsAccountApiRequest({
          action: value ? "toggle_status_active" : "toggle_status_inactive"
        })
      );
      AccountController.setAccountStatus(
        new AccountStatus({
          status: value
        })
      );
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, "Gagal Mengubah Status Akun !", () =>
          this.onClickToggleStatus(value)
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  nameUpdateFeature(name: any) {
    return updateFeatureName(name);
  }
  translatedFeatureName(name: any) {
    if (name && typeof name === "string" && name.includes(".")) {
      const direct = this.$t(name);
      if (direct !== name) return direct;
    }
    const displayName = updateFeatureName(name);
    const key = "featureName." + displayName;
    const t = this.$t(key);
    return t === key ? displayName : t;
  }

  get isDashboardDetail() {
    return this.$route.name === "dashboard-detail";
  }
  get shouldChangePasswordAt() {
    return AccountController.accountData.shouldChangePasswordAt;
  }

  async onHandleModalAction(
    meta: NotificationMetaLogin,
    actionType: ActionType
  ) {
    MainAppController.showLoading();
    MainAppController.closeMessageModal();

    const action = actionType.toLowerCase() as ActionType;
    const payload = new DeviceAuthReviewRequest(
      meta.deviceApprovalId,
      action,
      meta.deviceIdApproval
    );

    try {
      await AccountController.deviceAuthReview(payload);
      this.showActionToast(meta.deviceAccountName, action);
      ManagementLoginApprovalController.setRefreshList(true);
    } catch (error) {
      this.handleFailedConfirmation(error, meta, actionType);
    } finally {
      MainAppController.closeLoading();
    }
  }

  private showActionToast(accountName: string, action: ActionType) {
    const isApprove = action.toLowerCase() === "approve";

    const message = `
      <div style="color: ${isApprove ? "#00875A" : "#E40101"}">
        <b>${isApprove ? "Diterima" : "Ditolak"}</b><br />
        Permintaan login <b>${accountName}</b> telah Anda ${
      isApprove ? "terima" : "tolak"
    }.
      </div>
    `;

    MainAppController.setSnackbarMessage(message);
    MainAppController.setSnackbarPositivity(true);
    MainAppController.setSnackbarTimeout(5000);
    MainAppController.setSnackbarBackgroundColor(
      isApprove ? "#E3FCEF" : "#FEE8E8"
    );
    MainAppController.setSnackbarIconLeft(
      isApprove ? "success-status" : "failed-status"
    );
    MainAppController.setSnackbar(true);
  }

    getSharedNewsSnippet(
      createdBy: string | number,
      title: string,
      maxLength: number,
      useWideEllipsis = false
    ) {
      const creatorName = String(createdBy || "");
      const sharePrefix = ` ${this.$t("notification.sharedNewsPrefix")} `;
      const fullText = creatorName + sharePrefix + title;

      if (creatorName.length + title.length > maxLength) {
        const slicedText = fullText.substr(
          creatorName.length,
          maxLength - creatorName.length
        );
        return `${slicedText}${useWideEllipsis ? " . . . . ." : "..."}`;
      }

      return `${sharePrefix}${title.charAt(0).toUpperCase() + title.slice(1)}`;
    }

  handleFailedConfirmation(
    error: any,
    meta: NotificationMetaLogin,
    actionType: ActionType
  ) {
    const errorMessage = error?.response?.data?.message || "";
    const errorMessageId = error?.response?.data?.message?.id || "";
    const msg = errorMessageId.toLowerCase();

    const isAccepted = msg.includes("diterima");
    const isRejected = msg.includes("ditolak");

    if (isAccepted || isRejected) {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: msg,
          textSuccess: "OKE",
          onSubmit: () => MainAppController.closeMessageModal(),
          image: isAccepted ? "badge-confirmation-general" : "login-failed",
          isButtonFull: true
        })
      );
      return;
    } else {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          image: "new-badge-failed",
          title: "Gagal diproses",
          message: errorMessageId || error,
          textSuccess: "Coba lagi",
          textCancel: "Kembali",
          onClose: () => {
            MainAppController.closeMessageModal();
          },
          onSubmit: () => {
            MainAppController.closeMessageModal();
            ManagementLoginApprovalController.setRefreshList(true);
          }
        })
      );
    }
  }

  handleConfirmation(meta: NotificationMetaLogin, actionType: ActionType) {
    const actionLabel =
      actionType.toLowerCase() === "approve"
        ? this.$t("notification.approve")
        : this.$t("notification.reject");

    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title:
          actionType.toLowerCase() === "reject"
            ? this.$t("notification.rejectLoginRequest")
            : this.$t("notification.approveLoginRequest"),
        message:
          actionType.toLowerCase() === "reject"
            ? this.$t("notification.confirmRejectLoginRequest")
            : this.$t("notification.confirmApproveLoginRequest"),
        onSubmit: () => this.onHandleModalAction(meta, actionType),
        onClose: () => {
          MainAppController.closeMessageModal();
        },
        textSuccess: `Ya, ${actionLabel}`,
        textCancel: "Batalkan",
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6"
      })
    );
  }

  countUnread(value: number) {
    return value > 100 ? "99+" : value;
  }

  get permissionButtonActionReject() {
    return FcsPermissionManagementLoginApproval.permission_action_reject_enable.isEnabled();
  }

  get permissionButtonActionApprove() {
    return FcsPermissionManagementLoginApproval.permission_action_approve_enable.isEnabled();
  }

  isActiveHaveChildren(item: any) {
    return (
      item.meta.name === this.getCurrentRoute.meta.name ||
      this.nameUpdateFeature(item.meta.name) === this.getCurrentRoute.meta.name
    );
  }
}

export default Breadcrumb;
